import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { Caption, H6, Subtitle2 } from '@src/elements/typography/typography'
import draggable from 'vuedraggable'

import { Icon } from '@src/elements/icon/icon'
import { WorkoutCardConfig } from '@src/components/card/workout-card/workout-card-config/workout-card-config'
import { TrainingDay } from '@src/data/training-plan'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { Button } from '@src/elements/button/button'
import { defaultDaysNumber, disableDragIfModalsAreOpened, DRAG_COMMON_SETTING } from '@src/util/constants'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { Spacer } from '@src/elements/spacer/spacer'
import WorkoutData, { HolderType, WorkoutDataState } from '@src/data/workout-data'
import { HealthLimitationService } from '@src/service/health-limitation'
import { HealthLimitationSet } from '@src/data/health-limitation-set'
import SessionManager from '@src/lib/session_manager'

@Component({
  components: {
    draggable: draggable
  },
  computed: {
    trainingDays: {
      get: function () {
        return this.$store.state.new_training_plan.trainingDays || []
      },
      set: function (days) {
        // don't save swap if the immutable day place was changed (INVALID if a mutable day is followed by an immutable day)
        let validSwap = true
        let isMutableFound = false
        days.forEach((day) => {
          if (!day.immutable) isMutableFound = true
          else if (day.immutable && isMutableFound) validSwap = false
        })
        if (!validSwap) return
        // after drag and drop the inserted item and the first one has the same reference, so it's needed a full clone for training days array
        this.$store.state.new_training_plan.trainingDays = days.map((day, index) => new TrainingDay({ ...day, position: index + 1 }))
      }
    },
    positionToFocusBuilderOn: {
      get: function () {
        return this.$store.state.positionToFocusBuilderOn
      },
      set: function (value) {
        this.$store.state.positionToFocusBuilderOn = value
      }
    },
    member () {
      return this.$store.state.trainingMember
    },
    trainingPlanId () {
      return this.$store.state.new_training_plan.selectedTrainingPlan.id
    },
    completed () {
      return this.$store.state.new_training_plan.selectedTrainingPlan.completed
    }
  }
})
export class DaysBuilder extends Vue {
  @Prop({ required: false, default: false })
  disableEdit: boolean

  private trainingDays: TrainingDay[]

  private positionToFocusBuilderOn: number | null
  private completed: boolean

  private dragDayOptions: any = {
    group: {
      name: 'workoutSelector'
    },
    ...DRAG_COMMON_SETTING,
    chosenClass: 'dragged-exercise'
  }
  private dragDaysListOptions: any = {
    group: {
      name: 'trainingDay'
    },
    ...DRAG_COMMON_SETTING,
    chosenClass: 'dragged-exercise'
  }
  private loading: boolean = true

  private trainingPlanId: number
  private healthLimitations: HealthLimitationSet = new HealthLimitationSet([])

  created () {
    this.dragDaysListOptions.disabled = this.disableEdit
  }

  bringIntoView (index) {
    this.checkElement('trainingDay' + index).then(() => {
      document.getElementById('trainingDay' + index)!.scrollIntoView()
    })
  }

  updated () {
    this.$emit('confirmExitRequired')
    if (this.positionToFocusBuilderOn !== null) {
      this.bringIntoView(this.positionToFocusBuilderOn)
      this.positionToFocusBuilderOn = null
    }
  }

  rafAsync () {
    return new Promise(resolve => {
      requestAnimationFrame(resolve) // faster than set time out
    })
  }

  checkElement (selector) {
    if (document.getElementById(selector) === null) {
      return this.rafAsync().then(() => this.checkElement(selector))
    } else {
      return Promise.resolve(true)
    }
  }

  beforeMount () {
    window.onpopstate = () => { this.trainingDays = [] }

    if (this.trainingDays.length !== 0) {
      this.loading = false
      this.emptyTrashedDays()
    } else if (!this.trainingPlanId && !this.completed) {
      // completed means that we edit a TP before reactivating it - it's not a new TP
      // set default days if new TP
      this.setDefaultDays()
      this.loading = false
    }
  }

  emptyDay (day: TrainingDay) {
    const index = this.trainingDays.indexOf(day)

    this.trainingDays[index].id = null
    this.trainingDays[index].workout = null
    this.trainingDays[index].workoutAsArray = []
    this.trainingDays[index].restDays = 0
    this.$store.dispatch('markTrainingPlanAsUpdated')
  }

  emptyTrashedDays () {
    this.trainingDays.forEach(day => {
      if (day.workoutAsArray.length !== 0 && day.workoutAsArray[0].state === WorkoutDataState.TRASHED && !day.immutable) {
        this.emptyDay(day)
      }
    })
  }

  removeDaySlot (day: TrainingDay) {
    this.trainingDays.splice(day.position - 1, 1)
    let pos = 1
    this.trainingDays.forEach(day => {
      day.position = pos++
    })
    this.$store.dispatch('markTrainingPlanAsUpdated')
  }

  setDayWorkouts (day: TrainingDay, workouts: WorkoutData[]) {
    this.$set(day, 'workoutAsArray', workouts)
  }

  addDaySlot () {
    let day = new TrainingDay({
      id: 0,
      position: this.trainingDays.length + 1,
      restDays: 0,
      workout: null,
      workoutAsArray: []
    })
    this.trainingDays.push(day)
    this.$store.dispatch('markTrainingPlanAsUpdated')

    this.bringIntoView(this.trainingDays.length - 1)
  }

  render (h: CreateElement) {
    if (this.loading) {
      return <div class='builder'>
        <div class={'column-header'}>
          <H6 class='title'>{this.$t('PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.TITLE')}</H6>
          <Spacer/>
          <Loader />
        </div>
      </div>
    }

    let workoutCount = 0
    let totalMet = 0

    let index = -1
    const days = this.trainingDays.map((day) => {
      index++
      if (day.workoutAsArray.length > 0) {
        workoutCount += 1
        totalMet += day.workoutAsArray[0].estimatedMetValue

        if (!SessionManager.Instance.isManager() && day.workoutAsArray[0].hidden && day.workoutAsArray[0].holderType === HolderType.MEMBER) {
          return <div class={`day ${day.immutable ? 'empty-disabled' : ''}`} id={'trainingDay' + index}>
            <div class='day-placeholder'>
              <H6 class='day-number'>
                {this.$t('DAY')} {day.position}
                {day.immutable ? '' :
                  <Tooltip content={this.$t('PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.REMOVE_DAY_SLOT')}>
                    <Icon name='util/minus' class='trash-btn'
                          click={() => this.removeDaySlot(day)}/>
                  </Tooltip>
                }
              </H6>
              <Caption class='drag-drop'>{this.$t('PAGE.CONTENT.WORKOUT.MODAL.DRAG_DROP_WORKOUTS')}</Caption>
            </div>

            {/* a v-model on a loop variable compiles to this.$set inside the ES5 map callback, where this is undefined */}
            <draggable disabled={day.immutable} value={day.workoutAsArray} onInput={(workouts) => this.setDayWorkouts(day, workouts)} options={this.dragDayOptions} class={`draggable-zone ${day.immutable ? 'disabled' : ''}`} />
          </div>

        }

        return <WorkoutCardConfig
                  hidden={day.workoutAsArray[0].hidden && day.workoutAsArray[0].holderType === HolderType.MEMBER}
                  workoutId={day.workoutAsArray[0].id}
                  dayNumber={day.position}
                  removeWorkoutCallback={this.emptyDay}
                  day={day}
                  removeDayCallback={this.removeDaySlot}
                  id={'trainingDay' + index}
                  healthLimitations={this.healthLimitations}
              />

      } else {
        return <div class={`day ${day.immutable ? 'empty-disabled' : ''}`} id={'trainingDay' + index}>
          <div class='day-placeholder'>
            <H6 class='day-number'>
              {this.$t('DAY')} {day.position}
              {day.immutable ? '' :
                <Tooltip content={this.$t('PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.REMOVE_DAY_SLOT')}>
                  <Icon name='util/minus' class='trash-btn'
                        click={() => this.removeDaySlot(day)}/>
                </Tooltip>
              }
            </H6>
            <Caption class='drag-drop'>{this.$t('PAGE.CONTENT.WORKOUT.MODAL.DRAG_DROP_WORKOUTS')}</Caption>
          </div>

          {/* a v-model on a loop variable compiles to this.$set inside the ES5 map callback, where this is undefined */}
          <draggable disabled={day.immutable} value={day.workoutAsArray} onInput={(workouts) => this.setDayWorkouts(day, workouts)} options={this.dragDayOptions} class={`draggable-zone ${day.immutable ? 'disabled' : ''}`} />
        </div>
      }
    })

    return <div class='builder'>
      <div class={'column-header'}>
        <H6 class='title'>{this.$t('PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.TITLE')}</H6>
        <span>
          <Icon class='summary-icon' name='workout/exercises' />
          <Subtitle2>{`${workoutCount} `}{this.$t('WORKOUTS')}</Subtitle2>
          <Icon class='summary-icon' name='workout/time' />
          <Subtitle2>{`${this.trainingDays.length} `}{this.$t('DAYS')}</Subtitle2>
          <Icon class='summary-icon' name='workout/score' />
          <Subtitle2>{`${totalMet} `}{this.$t('UNITS.MET')}</Subtitle2>
      </span>
      </div>

      <Button class='add-day-btn' type='remove' icon={'util/add'} label={this.$t('PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.ADD_DAY_SLOT')} click={this.addDaySlot} />

      <draggable v-model={this.trainingDays} options={this.dragDaysListOptions} class='day-list' onChoose={disableDragIfModalsAreOpened}>
        {days}
      </draggable>
    </div>
  }

  setDefaultDays () {
    let trainingDays: TrainingDay[] = []
    for (let i = 0; i < defaultDaysNumber; i++) {
      let day = new TrainingDay({
        id: 0,
        position: i + 1,
        restDays: defaultDaysNumber - i - 1,
        workout: null,
        workoutAsArray: []
      })
      trainingDays.push(day)
    }
    this.$store.dispatch('setTrainingPlanDays', trainingDays)
  }

  @Watch('member', { deep: true, immediate: true })
  updateMemberWorkouts (newMember, oldMember) {
    const hasMemberChanged = (oldMember || newMember) && ((oldMember && !newMember) || (!oldMember && newMember) || (oldMember.id !== newMember.id))
    if (hasMemberChanged) {
      this.trainingDays.forEach(day => {
        if (day.workoutAsArray.length !== 0 && day.workoutAsArray[0].isMemberTrainerWorkout()) this.emptyDay(day)
      })
    }
    if (newMember) {
      return new HealthLimitationService().loadLatestHealthLimitations(newMember.id).then((limits) => this.healthLimitations = limits)
    } else this.healthLimitations = new HealthLimitationSet([])
  }
  @Watch('trainingDays', { deep: true })
  trainingPlanLoaded (newTrainingPlan, oldTrainingPlan) {
    if (this.loading && newTrainingPlan.length !== 0 && oldTrainingPlan.length === 0) {
      this.loading = false
      this.emptyTrashedDays()
    }
    if (newTrainingPlan.length === 0 && oldTrainingPlan.length !== 0) {
      this.trainingDays = oldTrainingPlan
    }
  }

}
